import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config';
import { ApiResponse, PaginatedResponse } from '../models';
import {
  Series,
  SeriesNavContext,
  SeriesRoute,
  CreateSeriesDto,
  UpdateSeriesDto,
  AddPostToSeriesDto,
  SeriesListParams,
} from '../models/series.model';

@Injectable({ providedIn: 'root' })
export class SeriesService {
  private readonly http = inject(HttpClient);
  private readonly _apiBase: string;
  private readonly _blogPostsBase: string;

  private readonly _readerBase: string;

  constructor() {
    const config = inject(CMS_POST_API_CONFIG);
    this._apiBase = `${config.apiBaseUrl}/api/series`;
    this._blogPostsBase = `${config.apiBaseUrl}/api/blog-posts`;
    this._readerBase = `${config.apiBaseUrl}/api/reader/series`;
  }

  // ─── Public (unauthenticated) ──────────────────────────────────────────────

  getPublicSeries(
    params: Omit<SeriesListParams, 'publishStatus'>
  ): Observable<PaginatedResponse<Series>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params) });
    return this.http.get<PaginatedResponse<Series>>(`${this._apiBase}/public`, { params: httpParams });
  }

  getPublicSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<Series>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http.get<ApiResponse<Series>>(`${this._apiBase}/public/${slug}`, { params: httpParams });
  }

  getPublicSeriesRoutes(orgId: string): Observable<ApiResponse<SeriesRoute[]>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http.get<ApiResponse<SeriesRoute[]>>(`${this._apiBase}/public/routes`, { params: httpParams });
  }

  getSeriesForPublicPost(postIdOrSlug: string): Observable<ApiResponse<SeriesNavContext[]>> {
    return this.http.get<ApiResponse<SeriesNavContext[]>>(
      `${this._blogPostsBase}/public/${postIdOrSlug}/series`
    );
  }

  // ─── Authenticated ─────────────────────────────────────────────────────────

  listSeries(params: SeriesListParams): Observable<PaginatedResponse<Series>> {
    const httpParams = new HttpParams({ fromObject: this._toStringParams(params) });
    return this.http.get<PaginatedResponse<Series>>(this._apiBase, { params: httpParams });
  }

  getSeriesById(id: string, orgId: string): Observable<ApiResponse<Series>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http.get<ApiResponse<Series>>(`${this._apiBase}/${id}`, { params: httpParams });
  }

  getSeriesForPost(postId: string): Observable<ApiResponse<SeriesNavContext[]>> {
    return this.http.get<ApiResponse<SeriesNavContext[]>>(
      `${this._blogPostsBase}/${postId}/series`
    );
  }

  createSeries(dto: CreateSeriesDto): Observable<ApiResponse<Series>> {
    return this.http.post<ApiResponse<Series>>(this._apiBase, dto);
  }

  updateSeries(id: string, dto: UpdateSeriesDto): Observable<ApiResponse<Series>> {
    return this.http.patch<ApiResponse<Series>>(`${this._apiBase}/${id}`, dto);
  }

  deleteSeries(id: string, orgId: string): Observable<{ success: boolean; message: string }> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http.delete<{ success: boolean; message: string }>(
      `${this._apiBase}/${id}`,
      { params: httpParams }
    );
  }

  addPostToSeries(seriesId: string, dto: AddPostToSeriesDto): Observable<ApiResponse<Series>> {
    return this.http.post<ApiResponse<Series>>(`${this._apiBase}/${seriesId}/posts`, dto);
  }

  removePostFromSeries(
    seriesId: string,
    postId: string,
    orgId: string
  ): Observable<ApiResponse<Series>> {
    const httpParams = new HttpParams().set('orgId', orgId);
    return this.http.delete<ApiResponse<Series>>(
      `${this._apiBase}/${seriesId}/posts/${postId}`,
      { params: httpParams }
    );
  }

  // ─── Reader (authenticated, visibility-filtered) ───────────────────────────

  getReaderSeries(orgId: string, page?: number, limit?: number): Observable<PaginatedResponse<Series>> {
    let params = new HttpParams().set('orgId', orgId);
    if (page !== undefined) params = params.set('page', String(page));
    if (limit !== undefined) params = params.set('limit', String(limit));
    return this.http.get<PaginatedResponse<Series>>(this._readerBase, { params });
  }

  getReaderSeriesBySlug(slug: string, orgId: string): Observable<ApiResponse<Series>> {
    const params = new HttpParams().set('orgId', orgId);
    return this.http.get<ApiResponse<Series>>(`${this._readerBase}/${slug}`, { params });
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private _toStringParams(obj: SeriesListParams | Omit<SeriesListParams, 'publishStatus'>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
  }
}
